//  src/Pages/Admin/AllowedLocationsMap.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout      from '../../Components/Layout/Layout';
import { API }     from '../../utils/api';
import {
  Box,
  List, ListSubheader,
  ListItem, ListItemButton, ListItemText,
  Typography, TextField, Button
} from '@mui/material';
import {
  MapContainer, TileLayer,
  FeatureGroup, Marker, Circle, Polygon, Popup, useMap
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';

/* ──────────────── DRAW CONTROL ───────────────── */
function DrawControl({ onCreate, onEdit, onDelete }) {
  const map = useMap();
  const fg  = useRef(new L.FeatureGroup());

  useEffect(() => {
    map.addLayer(fg.current);

    const ctrl = new L.Control.Draw({
      draw : {
        rectangle:false, circlemarker:false, polyline:false, circle:false,
        marker:true, polygon:{ allowIntersection:false }
      },
      edit : { featureGroup: fg.current, remove:true, edit:true }
    });
    map.addControl(ctrl);

    map.on(L.Draw.Event.CREATED,  onCreate);
    map.on(L.Draw.Event.EDITED,   onEdit);
    map.on(L.Draw.Event.DELETED,  onDelete);

    return () => {
      map.off(L.Draw.Event.CREATED,  onCreate);
      map.off(L.Draw.Event.EDITED,   onEdit);
      map.off(L.Draw.Event.DELETED,  onDelete);
      map.removeControl(ctrl);
    };
  }, [map, onCreate, onEdit, onDelete]);

  return <FeatureGroup ref={fg} />;
}
/* ─────────────────────────────────────────────── */

export default function AllowedLocationsMap() {
  const [points, setPoints] = useState([]);
  const [polys,  setPolys]  = useState([]);
  const [sel,    setSel]    = useState({ type:null, id:null });
  const [drafts, setDrafts] = useState({});
  const [polyDrafts, setPolyDrafts] = useState({});

  /* ── load from API ───────────────────────────── */
  const load = useCallback(async () => {
    const { points:pts, polygons } =
      await (await fetch(API('/api/admin/allowed-locations'))).json();

    setPoints(
      pts.map(p => ({
        id:p.id, lat:p.lat, lng:p.lng, radius:p.radius,
        name:p.name || `Zone #${p.id}`
      }))
    );
    setPolys(
      polygons.map(a => ({
        id:a.id, paths:a.paths,
        name:a.name || `Area #${a.id}`
      }))
    );
  }, []);
  useEffect(() => { load(); }, [load]);

  /* ── helpers to backend ───────────────────────── */
  const updatePoint = (id, patch) =>
    fetch(API(`/api/admin/allowed-locations/point/${id}`),{
      method:'PUT', headers:{'Content-Type':'application/json'},
      body:JSON.stringify(patch)
    }).then(()=> setPoints(ps=>ps.map(p=>p.id===id?{...p,...patch}:p)));

  const deletePoint = id =>
    fetch(API(`/api/admin/allowed-locations/point/${id}`),{method:'DELETE'})
      .then(()=> setPoints(ps=>ps.filter(p=>p.id!==id)));

  const updatePoly = (id, patch) =>
    fetch(API(`/api/admin/allowed-locations/polygon/${id}`),{
      method:'PUT', headers:{'Content-Type':'application/json'},
      body:JSON.stringify(patch)
    }).then(()=> setPolys(ps=>ps.map(a=>a.id===id?{...a,...patch}:a)));

  const deletePoly = id =>
    fetch(API(`/api/admin/allowed-locations/polygon/${id}`),{method:'DELETE'})
      .then(()=> setPolys(ps=>ps.filter(a=>a.id!==id)));

  /* ── create via draw ──────────────────────────── */
  const handleCreate = async e => {
    if (e.layerType==='marker') {
      const {lat,lng} = e.layer.getLatLng();
      const o = await (await fetch(API('/api/admin/allowed-locations/point'),{
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({lat,lng,radius:100,name:''})
      })).json();
      setPoints(ps=>[...ps,{...o,name:o.name||`Zone #${o.id}`}]);
    }
    if (e.layerType==='polygon') {
      const paths = e.layer.getLatLngs()[0].map(p=>[p.lat,p.lng]);
      const o = await (await fetch(API('/api/admin/allowed-locations/polygon'),{
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({paths,name:''})
      })).json();
      setPolys(ps=>[...ps,{...o,name:o.name||`Area #${o.id}`}]);
    }
  };

  const handleEdit = e => {
    e.layers.eachLayer(l => {
      const m = l.options.meta; if(!m) return;
      if (m.type==='point') {
        const {lat,lng} = l.getLatLng();
        updatePoint(m.id,{ latitude:lat, longitude:lng });
      } else {
        const paths = l.getLatLngs()[0].map(p=>[p.lat,p.lng]);
        updatePoly(m.id,{ paths });
      }
    });
  };
  const handleDelete = e => {
    e.layers.eachLayer(l => {
      const m = l.options.meta; if(!m) return;
      m.type==='point' ? deletePoint(m.id) : deletePoly(m.id);
    });
  };

  /* ── drafts helpers ───────────────────────────── */
  const setPointDraft = (id, fld, val) =>
    setDrafts(d => ({ ...d, [id]:{ ...d[id], [fld]:val }}));
  const setPolyDraft  = (id, val) =>
    setPolyDrafts(d => ({ ...d, [id]:{ name:val }}));

  const commitPoint = id => {
    const d = drafts[id]; if(!d) return;
    const patch = {};
    if (d.name   !== undefined) patch.name   = d.name;
    if (d.radius !== undefined) patch.radius = +d.radius;
    if (Object.keys(patch).length) updatePoint(id,patch);
    setDrafts(o=>{const q={...o}; delete q[id]; return q;});
  };
  const commitPoly = id => {
    const d = polyDrafts[id]; if(!d) return;
    if (d.name !== undefined) updatePoly(id,{ name:d.name });
    setPolyDrafts(o=>{const q={...o}; delete q[id]; return q;});
  };

  /* ── UI ────────────────────────────────────────── */
  return (
    <Layout>
      <Box sx={{display:'flex',height:'calc(100vh - 64px)'}}>

        {/* sidebar */}
        <Box sx={{width:280,p:2,borderRight:'1px solid #ddd',overflowY:'auto'}}>
          <Typography variant="h6">Circles</Typography>
          <List dense subheader={<ListSubheader>Circular zones</ListSubheader>}>
            {points.map(p=>(
              <ListItem disablePadding key={p.id}>
                <ListItemButton
                  selected={sel.type==='point'&&sel.id===p.id}
                  onClick={()=>setSel({type:'point',id:p.id})}
                >
                  <ListItemText
                    primary={p.name}
                    secondary={`R: ${p.radius} m`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Typography variant="h6" sx={{mt:2}}>Polygons</Typography>
          <List dense subheader={<ListSubheader>Areas</ListSubheader>}>
            {polys.map(a=>(
              <ListItem disablePadding key={a.id}>
                <ListItemButton
                  selected={sel.type==='polygon'&&sel.id===a.id}
                  onClick={()=>setSel({type:'polygon',id:a.id})}
                >
                  <ListItemText
                    primary={a.name}
                    secondary={`${a.paths.length} points`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* map */}
        <Box sx={{flex:1}}>
          <MapContainer
            center={[25.2,55.3]}
            zoom={13}
            style={{height:'100%',width:'100%'}}
          >
            <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"/>
            <DrawControl
              onCreate={handleCreate}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />

            {/* circles */}
            {points.map(p=>{
              const d       = drafts[p.id] || {};
              const name    = d.name   ?? p.name;
              const radius  = d.radius ?? p.radius;
              const meta    = { id:p.id, type:'point' };

              return (
                <React.Fragment key={p.id}>
                  <Circle
                    center={[p.lat,p.lng]}
                    radius={radius}
                    pathOptions={{
                      color: sel.type==='point'&&sel.id===p.id?'orange':'green',
                      fillOpacity:0.2
                    }}
                    options={{ meta }}
                    eventHandlers={{ popupclose:()=>commitPoint(p.id) }}
                  >
                    <Popup minWidth={260}>
                      <TextField
                        label="Name" fullWidth size="small" sx={{mb:1}}
                        value={name}
                        onChange={e=>setPointDraft(p.id,'name',e.target.value)}
                      />
                      <TextField
                        label="Radius (m)" fullWidth size="small" type="number"
                        value={radius}
                        onChange={e=>{
                          const v=+e.target.value;
                          if(v>0) setPointDraft(p.id,'radius',v);
                        }}
                      />
                      <Box textAlign="right" mt={1}>
                        <Button
                          color="error" size="small"
                          onClick={()=>deletePoint(p.id)}
                        >Delete</Button>
                      </Box>
                    </Popup>
                  </Circle>

                  <Marker
                    position={[p.lat,p.lng]}
                    draggable
                    icon={L.divIcon({
                      className:'point-label',
                      html:`<div class="plabel">${name}</div>`,
                      iconSize:null
                    })}
                    eventHandlers={{
                      dragend:ev=>{
                        const {lat,lng}=ev.target.getLatLng();
                        updatePoint(p.id,{ latitude:lat, longitude:lng });
                      }
                    }}
                    options={{ meta }}
                  />
                </React.Fragment>
              );
            })}

            {/* polygons */}
            {polys.map(poly=>{
              const d    = polyDrafts[poly.id] || {};
              const name = d.name ?? poly.name;
              const meta = { id:poly.id, type:'polygon' };

              const { lat:sumLat, lng:sumLng } = poly.paths
                .reduce((acc,[la,lo])=>({lat:acc.lat+la,lng:acc.lng+lo}),
                        {lat:0,lng:0});
              const cLat = sumLat / poly.paths.length;
              const cLng = sumLng / poly.paths.length;

              return (
                <React.Fragment key={poly.id}>
                  <Polygon
                    positions={poly.paths}
                    pathOptions={{
                      color: sel.type==='polygon'&&sel.id===poly.id?'orange':'blue',
                      fillOpacity:0.2
                    }}
                    options={{ meta }}
                    eventHandlers={{ popupclose:()=>commitPoly(poly.id) }}
                  >
                    <Popup minWidth={260}>
                      <TextField
                        label="Name" fullWidth size="small"
                        value={name}
                        onChange={e=>setPolyDraft(poly.id,e.target.value)}
                      />
                      <Box textAlign="right" mt={1}>
                        <Button
                          color="error" size="small"
                          onClick={()=>deletePoly(poly.id)}
                        >Delete</Button>
                      </Box>
                    </Popup>
                  </Polygon>

                  <Marker
                    position={[cLat,cLng]}
                    interactive={false}
                    icon={L.divIcon({
                      className:'poly-label',
                      html:`<div class="plabel">${name}</div>`,
                      iconSize:null
                    })}
                  />
                </React.Fragment>
              );
            })}

          </MapContainer>
        </Box>
      </Box>
    </Layout>
  );
}
